import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../colors";
import DarkHeroPageEffects from "../DarkHeroPageEffects";
import { PageContainer } from "../PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";

const heroSectionSx = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: HERO_BG,
  px: { xs: 2, md: 4 },
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  pb: { xs: "80px", md: "96px" },
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const REVEAL_TRANSITION = { duration: 0.9, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const sectionSx = {
  py: { xs: "80px", md: "120px" },
  px: { xs: 2, md: 4 },
  borderTop: `1px solid ${colors.border.subtle}`,
  backgroundColor: HERO_BG,
};

const cardSx = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
  border: "1px solid rgba(255,255,255,0.04)",
  borderRadius: "18px",
};

const titleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 2,
};

const descSx = {
  maxWidth: "34rem",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  color: colors.text.secondary,
  mb: { xs: 6, md: 8 },
};

const btnPrimarySx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.contrast,
  background: colors.primary.main,
  px: "32px",
  py: "14px",
  textDecoration: "none",
  clipPath: "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
  border: "1px solid transparent",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s ease, border-color .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
    transform: "translateX(-120%)",
    transition: "transform .6s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,255,255,0.18)",
    boxShadow: "0 10px 30px rgba(232,163,61,0.18)",
  },
  "&:hover::before": { transform: "translateX(120%)" },
  "&:active": { transform: "translateY(0px)" },
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background: "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": { opacity: 1 },
  "&:active": { transform: "translateY(0px)" },
};

const GOLD_GRADIENT = "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)";
const DANGER_GRADIENT = `linear-gradient(180deg, #f87171 0%, ${colors.state.danger.main} 100%)`;

const FLAGGED_TICK_IDX = [44, 45, 46, 47, 62, 63, 64, 88, 89, 90, 91, 92];
const SUSPICIOUS_TICK_IDX = [40, 41, 42, 43, 48, 58, 59, 60, 61, 65, 84, 85, 86, 87, 93, 94];

const STATS = [
  {
    sublabel: "Collection frequency",
    num: "Every",
    sup: "tick",
    label:
      "Gameplay data captured continuously — not sampled, not batched after the match ends. Live.",
  },
  {
    sublabel: "Data origin",
    num: "Server",
    sup: "only",
    label:
      "All critical signals come from the match server — not from clips, client reports, or anything a cheater can shape around.",
  },
  {
    sublabel: "Cheat bypass risk",
    num: "Zero",
    sup: "from client",
    label:
      "It doesn't matter what software or hardware runs on the player's machine. The server collects its own data independently.",
  },
];

const SERVER_POINTS = [
  "Cannot be tampered with from the player's machine",
  "Full engagement timeline — every tick, every event",
  "Immune to DMA hardware cheats by design",
  "No software installed on your computer",
  "Pattern analysis runs with full match context",
  "Combined with identity-backed bans — no easy reset",
];

const CLIENT_POINTS = [
  "Cheat developers study and bypass each update",
  "Kernel drivers still bypassed by hardware DMA cheats",
  "Creates deep OS-level access on your machine",
  "Requires trust that the software itself is safe",
  "Arms race: publish update → reverse-engineer → repeat",
  "Complementary layer only — never the full answer",
];

const SIGNALS = [
  {
    num: "01",
    flaggable: true,
    name: "Prefire consistency",
    body: "Angles that repeatedly prefired with precision beyond what map knowledge and timing alone could produce. Evaluated across the full match — not isolated to a single clip.",
    note: "Pattern frequency, angle deviation, consistency against legitimate high-level benchmarks.",
    icon: (
      <>
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35M11 8v3l2 2" />
      </>
    ),
  },
  {
    num: "02",
    flaggable: true,
    name: "Wall hits & visibility timing",
    body: "Damage or hits that occur before valid visibility is established according to server geometry, or that travel through surfaces not explained by penetration mechanics.",
    note: "Server-confirmed visibility state at the exact tick of each damage event.",
    icon: (
      <>
        <path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m5.66 5.66 4.24 4.24M2 12h6m8 0h6M4.93 19.07l4.24-4.24m5.66-5.66 4.24-4.24" />
      </>
    ),
  },
  {
    num: "03",
    flaggable: true,
    name: "Trigger speed",
    body: "Reaction times and trigger behavior that fall consistently below human-achievable thresholds — or display mechanical precision that doesn't vary the way human reaction does.",
    note: "Reaction time distributions, variance profiles, input-to-output latency patterns.",
    icon: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />,
  },
  {
    num: "04",
    flaggable: true,
    name: "Kill chain patterns",
    body: "Multi-kill sequences where the time-to-kill, movement corrections between targets, and transition timing collectively fall outside the range of legitimate high-performance play.",
    note: "Inter-kill timing, aim correction velocity between targets, TTK distributions over time.",
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
  {
    num: "05",
    flaggable: true,
    name: "Repeated outlier patterns",
    body: "Single moments can be explainable. Patterns over time, across multiple rounds and matches, that sit consistently outside the legitimate performance distribution cannot be.",
    note: "Cross-match consistency, deviation from per-player baselines, comparison to verified legitimate players at the same ELO.",
    icon: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  },
  {
    num: "+",
    flaggable: false,
    name: "Private checks",
    body: "The five categories above are publicly disclosed to help players understand the system. The full detection library is not published — and won't be — because transparency to cheaters works against everyone.",
    note: "Publishing detection logic gives cheat developers a direct roadmap. Naming categories helps honest players understand the system without arming bad actors.",
    noteMuted: true,
    icon: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
  },
];

const DMA_POINTS = [
  {
    title: "What DMA is",
    body: "Direct Memory Access. A separate hardware device — often a PCIe card or Thunderbolt adapter — that reads game memory from outside the operating system. The game runs normally. The cheat runs on different hardware entirely.",
    icon: (
      <>
        <rect x="2" y="2" width="12" height="10" rx="1" />
        <path d="M6 12v2M10 12v2M4 14h8" />
      </>
    ),
  },
  {
    title: "Why it defeats kernel drivers",
    body: "Even a kernel-level anti-cheat driver runs inside the operating system. DMA reads happen below the OS layer. No software — including ring-0 drivers — can reliably detect reads that occur at the hardware level, outside its own visibility.",
    icon: <path d="M8 2l2.06 4.17L15 7.18l-3.5 3.41.83 4.82L8 13.17l-4.33 2.24.83-4.82L1 7.18l4.94-.01L8 2z" />,
  },
  {
    title: "Why server-side is immune",
    body: "The server doesn't care what hardware or software is running on the client machine. It collects its own gameplay data independently. A DMA device can read the game's memory — but it cannot edit what the server records about what actually happened in the match.",
    icon: (
      <>
        <circle cx="8" cy="8" r="6" />
        <path d="M8 5v3l2 1" />
      </>
    ),
  },
  {
    title: "The cost barrier is falling",
    body: "DMA setups that cost thousands a few years ago are now available for a few hundred euros. The cheat hardware barrier is disappearing. Platforms that depend on client-side detection are increasingly exposed — and the gap will keep widening.",
    icon: (
      <>
        <path d="M2 8s3-5 6-5 6 5 6 5-3 5-6 5-6-5-6-5z" />
        <circle cx="8" cy="8" r="2" />
      </>
    ),
  },
];

const DMA_LAYERS = [
  {
    name: "CS2 Game Process",
    detail: "Memory: positions, states, angles",
    tag: "Visible to DMA",
    tagStyle: "bypassed",
    layerSx: {
      background: "rgba(255,255,255,0.04)",
      border: `1px solid ${colors.border.subtle}`,
      borderBottom: "none",
    },
    nameColor: colors.text.primary,
  },
  {
    name: "Operating System",
    detail: "Windows / Linux userspace",
    tag: "Bypassed",
    tagStyle: "bypassed",
    layerSx: {
      background: "rgba(255,255,255,0.02)",
      border: `1px solid ${colors.border.subtle}`,
      borderBottom: "none",
    },
    nameColor: colors.text.secondary,
  },
  {
    name: "Kernel / Ring-0 Driver",
    detail: "Anti-cheat kernel driver lives here",
    tag: "Bypassed",
    tagStyle: "bypassed",
    layerSx: {
      background: "rgba(255,255,255,0.015)",
      border: `1px solid ${colors.border.subtle}`,
      borderBottom: "none",
    },
    nameColor: colors.text.secondary,
  },
  {
    name: "DMA Hardware Device",
    detail: "PCIe / Thunderbolt — reads below OS",
    tag: "Undetectable",
    tagStyle: "blocked",
    layerSx: {
      background: "rgba(220,38,38,0.08)",
      border: "1px solid rgba(220,38,38,0.2)",
    },
    nameColor: colors.state.danger.main,
    detailColor: "rgba(220,38,38,0.6)",
  },
];

const ACTION_STEPS = [
  {
    num: "Step 01",
    title: "Live collection",
    body: "Gameplay data arrives continuously from the server throughout the match — every tick, every event, in real time. Collection starts at match start. It never stops until the server shuts down.",
    icon: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  },
  {
    num: "Step 02",
    title: "Pattern flagging",
    body: "Events are compared against the detection library — the public categories and the private checks. Suspicious sequences are flagged and queued for the next stage. False positives here are expected and filtered out.",
    icon: (
      <>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </>
    ),
  },
  {
    num: "Step 03",
    title: "Context review",
    body: "Flagged behavior is evaluated with full match context — what happened before the suspicious event, what happened after, and how it sits against the player's established baseline across their history on the platform.",
    icon: (
      <>
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </>
    ),
  },
  {
    num: "Step 04",
    title: "Action follows evidence",
    body: "Decisions are made when the evidence is strong enough — not when a single clip looks suspicious. Bans are identity-backed and not easily reversed. The combination of server data, account binding, and reputation history means consequences are real.",
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
];

const FAQ_ITEMS = [
  {
    q: "Does the anti-cheat run during live matches?",
    a: "Yes. Server-side gameplay data is collected continuously throughout every match — not post-processed after it ends. Suspicious patterns are flagged as they emerge.",
  },
  {
    q: "Is this based on reports or clips?",
    a: "No. Player reports can direct admin attention, but the anti-cheat itself is built around server-side event collection and pattern analysis. Clips are fragments; the server sees the full timeline.",
  },
  {
    q: "Do you publish every detection pattern?",
    a: "No. The public categories exist so players understand what's being measured. The full pattern library and decision logic stay private — publishing them would give cheat developers a direct roadmap.",
  },
  {
    q: "Does the system stay static?",
    a: "No. New patterns are added and tested continuously as more gameplay data flows through the system. Long-term verified players help build a stronger baseline for what legitimate high-level play actually looks like.",
  },
  {
    q: "Is a client-side layer being added?",
    a: "Yes — a complementary client-side layer is in active development. It's scoped to cover specific checks server-side structurally cannot see. Server-side remains the foundation. Ouro is not betting the platform on a kernel driver.",
  },
  {
    q: "What is a DMA cheat?",
    a: "A separate hardware device — often a PCIe card or Thunderbolt adapter — that reads game memory from outside the operating system. Software running on the player's computer, including kernel-level drivers, cannot reliably detect reads that happen below the OS layer. Server-side detection is immune because it doesn't rely on anything the client does or doesn't do.",
  },
];

const STACK_ITEMS = [
  {
    name: "Anti-Cheat",
    body: "Server-side pattern detection on every tick. Can't be interfered with from the client. Paired with identity-backed bans so consequences actually stick.",
    active: true,
  },
  {
    name: "Trust & Safety",
    body: "Verification, moderation, reporting, and conduct policy. The layer that controls who gets in — and keeps the accountability loop running after they're here.",
    to: "/trust-and-safety",
    linkLabel: "See trust & safety →",
  },
  {
    name: "Reputation",
    body: "A permanent record across six dimensions. Behavior over time — not single incidents. Anti-cheat bans feed directly into the reputation system.",
    to: "/reputation",
    linkLabel: "See reputation →",
  },
];

function Eyebrow({ children, pulse }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2 }}>
      {pulse ? (
        <Box
          aria-hidden
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: colors.state.danger.main,
            flexShrink: 0,
            "@keyframes pulseDot": {
              "0%, 100%": { opacity: 1, transform: "scale(1)" },
              "50%": { opacity: 0.4, transform: "scale(0.7)" },
            },
            animation: "pulseDot 2s ease-in-out infinite",
          }}
        />
      ) : (
        <Box sx={{ width: 24, height: "1px", background: colors.primary.hover }} />
      )}
      <Typography
        sx={{
          fontSize: { xs: "0.7rem", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: colors.primary.hover,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionIntro({ label, title, description }) {
  return (
    <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
      <Eyebrow>{label}</Eyebrow>
      <Typography component="h2" sx={titleSx}>
        {title}
      </Typography>
      {description ? <Typography sx={descSx}>{description}</Typography> : null}
    </MotionBox>
  );
}

function SvgIcon({ children, size = 28, sx = {} }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      sx={{ width: size, height: size, flexShrink: 0, color: colors.primary.main, ...sx }}
    >
      {children}
    </Box>
  );
}

function TickTrack() {
  const bars = React.useMemo(() => {
    return Array.from({ length: 120 }, (_, i) => {
      let kind = "normal";
      if (FLAGGED_TICK_IDX.includes(i)) kind = "flagged";
      else if (SUSPICIOUS_TICK_IDX.includes(i)) kind = "suspicious";
      return { kind, height: Math.floor(Math.random() * 52) + 12 };
    });
  }, []);

  const barColors = {
    normal: colors.border.default,
    suspicious: "rgba(232,163,61,0.5)",
    flagged: colors.state.danger.main,
  };

  return (
    <Box sx={{ display: "flex", gap: "3px", alignItems: "flex-end", height: 72, mb: 2 }}>
      {bars.map((bar, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            minHeight: 4,
            height: bar.height,
            borderRadius: "1px 1px 0 0",
            backgroundColor: barColors[bar.kind],
          }}
        />
      ))}
    </Box>
  );
}

function VsList({ items, variant }) {
  return (
    <Box>
      {items.map((text) => (
        <Box
          key={text}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            py: 1.5,
            borderBottom: `1px solid ${colors.border.subtle}`,
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: variant === "client" && text.includes("Complementary") ? colors.text.muted : colors.text.secondary,
            "&:last-of-type": { borderBottom: "none" },
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0,
              mt: "5px",
              backgroundColor: variant === "server" ? colors.primary.main : colors.text.muted,
            }}
          />
          {text}
        </Box>
      ))}
    </Box>
  );
}

export default function AntiCheatPage() {
  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box component="header" sx={heroSectionSx}>
        <DarkHeroPageEffects watermark="ANTI-CHEAT" />

        <Box sx={{ position: "relative", zIndex: 2, maxWidth: "75rem", mx: "auto", width: "100%" }}>
          <MotionBox {...HERO_ENTER} transition={REVEAL_TRANSITION}>
            <Eyebrow pulse>Anti-Cheat — Live</Eyebrow>
          </MotionBox>

          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.075em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: colors.text.primary,
              maxWidth: 980,
              mb: 4,
            }}
          >
            Server-side.
            <br />
            Tick by{" "}
            <Box
              component="span"
              sx={{
                background: GOLD_GRADIENT,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              tick.
            </Box>
            <br />
            Can&apos;t be{" "}
            <Box
              component="span"
              sx={{
                background: DANGER_GRADIENT,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              touched.
            </Box>
          </MotionBox>

          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                lineHeight: 1.75,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "34rem",
                mb: 6,
              }}
            >
              Ouro&apos;s anti-cheat doesn&apos;t live on your machine — it lives on the server. Every tick of
              every match is collected, evaluated, and stored independently of anything a cheater could tamper
              with.
            </Typography>
          </MotionBox>

          <MotionBox
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.3 }}
            sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join the founding community
            </Box>
            <Box component={Link} to="/trust-and-safety" sx={btnGhostSx}>
              Trust &amp; Safety →
            </Box>
          </MotionBox>
        </Box>
      </Box>

      <Box component="section" sx={{ ...sectionSx, py: { xs: 0, md: 0 }, pt: { xs: 6, md: 8 }, borderTop: "none" }}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <MotionBox
            {...REVEAL}
            transition={REVEAL_TRANSITION}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              border: `1px solid ${colors.border.subtle}`,
            }}
          >
            {STATS.map((stat, i) => (
              <Box
                key={stat.sublabel}
                sx={{
                  p: { xs: "28px 24px", md: "40px 44px" },
                  borderRight: { md: i < 2 ? `1px solid ${colors.border.subtle}` : "none" },
                  borderBottom: { xs: i < 2 ? `1px solid ${colors.border.subtle}` : "none", md: "none" },
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background: i === 0 ? colors.primary.main : colors.border.subtle,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    mb: 1,
                  }}
                >
                  {stat.sublabel}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    fontWeight: 700,
                    lineHeight: 1,
                    color: colors.text.primary,
                    mb: 1,
                  }}
                >
                  {stat.num}
                  <Box
                    component="sup"
                    sx={{
                      fontSize: "0.45em",
                      verticalAlign: "top",
                      pt: "0.15em",
                      color: colors.primary.main,
                      ml: 0.25,
                    }}
                  >
                    {stat.sup}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.5 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Why it matters"
            title="The architecture decision."
            description="Most platforms install something on your computer and hope it catches cheats before they run. Ouro inverts that entirely — server-side is the foundation, not an afterthought."
          />

          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
              gap: { xs: 2, md: 0 },
            }}
          >
            <Box
              sx={{
                ...cardSx,
                borderRadius: "18px",
                p: { xs: "32px 28px", md: "48px 52px" },
                background:
                  "linear-gradient(135deg, rgba(232,163,61,0.04) 0%, transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
                borderColor: "rgba(232,163,61,0.25)",
              }}
            >
              <Typography
                sx={{
                  display: "inline-block",
                  fontSize: "0.5625rem",
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                  background: "rgba(232,163,61,0.1)",
                  border: "1px solid rgba(232,163,61,0.25)",
                  px: 1.25,
                  py: 0.5,
                  mb: 3,
                }}
              >
                Ouro — Primary
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: colors.text.primary,
                  mb: 1.5,
                }}
              >
                Server-side detection
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.75, mb: 3.5 }}>
                Data is collected by the server during the live match. Nothing the player does locally — no
                software, no hardware, no driver — can interfere with what the server sees and records.
              </Typography>
              <VsList items={SERVER_POINTS} variant="server" />
            </Box>

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                width: "1px",
                background: colors.border.subtle,
                position: "relative",
                my: 4,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "0.8125rem",
                  fontWeight: 700,
                  letterSpacing: "0.15em",
                  color: colors.text.muted,
                  background: HERO_BG,
                  px: 1,
                  py: 1,
                }}
              >
                VS
              </Typography>
            </Box>

            <Box sx={{ ...cardSx, borderRadius: "18px", p: { xs: "32px 28px", md: "48px 52px" } }}>
              <Typography
                sx={{
                  display: "inline-block",
                  fontSize: "0.5625rem",
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                  background: "rgba(115,115,125,0.3)",
                  border: "1px solid rgba(115,115,125,0.5)",
                  px: 1.25,
                  py: 0.5,
                  mb: 3,
                }}
              >
                Industry standard
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: colors.text.primary,
                  mb: 1.5,
                }}
              >
                Client-side detection
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.75, mb: 3.5 }}>
                A program installs on the player&apos;s machine and tries to detect cheat software before it runs.
                Even kernel-level drivers with full OS access face a constant arms race — and regularly lose it.
              </Typography>
              <VsList items={CLIENT_POINTS} variant="client" />
            </Box>
          </MotionBox>

          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.18 }}
            sx={{
              ...cardSx,
              mt: 8,
              p: { xs: "32px 28px", md: "48px" },
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: `linear-gradient(90deg, ${colors.primary.main} 0%, transparent 60%)`,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
              <Box
                aria-hidden
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: colors.state.danger.main,
                  "@keyframes pulseDot": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.4, transform: "scale(0.7)" },
                  },
                  animation: "pulseDot 2s ease-in-out infinite",
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.5625rem",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                }}
              >
                Live match data stream — every tick visualised
              </Typography>
            </Box>
            <TickTrack />
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              {[
                { label: "Normal play", color: colors.border.default },
                { label: "Suspicious pattern", color: "rgba(232,163,61,0.5)" },
                { label: "Flagged for review", color: colors.state.danger.main },
              ].map((item) => (
                <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "1px", backgroundColor: item.color }} />
                  <Typography sx={{ fontSize: "0.6875rem", color: colors.text.muted }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Detection signals"
            title="What the system watches for."
            description="These are the representative categories — not the full list. The complete pattern library stays private to prevent cheaters from studying around it."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
              gap: "2px",
            }}
          >
            {SIGNALS.map((signal) => (
              <Box
                key={signal.num}
                sx={{
                  ...cardSx,
                  borderRadius: 0,
                  p: { xs: "28px 24px", md: "36px 32px" },
                  position: "relative",
                  overflow: "hidden",
                  transition: "border-color .3s",
                  "&:hover": { borderColor: "rgba(255,255,255,0.08)" },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background: signal.flaggable ? colors.state.danger.main : colors.border.subtle,
                  },
                }}
              >
                <Typography
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 20,
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "4rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.03)",
                    lineHeight: 1,
                  }}
                >
                  {signal.num}
                </Typography>
                <SvgIcon sx={{ mb: 2.25 }}>{signal.icon}</SvgIcon>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.0625rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: colors.text.primary,
                    mb: 1.25,
                  }}
                >
                  {signal.name}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.7, mb: 2.25 }}>
                  {signal.body}
                </Typography>
                <Box sx={{ pt: 1.75, borderTop: `1px solid ${colors.border.subtle}` }}>
                  <Typography
                    sx={{
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: signal.noteMuted ? colors.text.muted : colors.state.danger.main,
                      mb: 0.5,
                    }}
                  >
                    {signal.noteMuted ? "Why private" : "What we look for"}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.6875rem",
                      color: colors.text.muted,
                      lineHeight: 1.6,
                    }}
                  >
                    {signal.note}
                  </Typography>
                </Box>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="The arms race"
            title="Why DMA cheats broke client-side."
            description="At the extreme end of the cheating landscape, hardware-based DMA devices bypass client-side detection entirely. This is the structural problem that drove Ouro's architecture decision."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 5, md: 8 },
              alignItems: "start",
            }}
          >
            <Box>
              {DMA_POINTS.map((point) => (
                <Box
                  key={point.title}
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    py: 2.25,
                    borderBottom: `1px solid ${colors.border.subtle}`,
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <Box
                    component="svg"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    sx={{ width: 16, height: 16, flexShrink: 0, mt: "2px", color: colors.primary.main }}
                  >
                    {point.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: colors.text.primary, mb: 0.5 }}>
                      {point.title}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.65 }}>
                      {point.body}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                background: `linear-gradient(180deg, rgba(220,38,38,0.04), transparent)`,
                border: "1px solid rgba(220,38,38,0.2)",
                borderRadius: "18px",
                p: { xs: "28px 24px", md: "40px" },
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  background: colors.state.danger.main,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    backgroundColor: colors.state.danger.main,
                    animation: "pulseDot 2s ease-in-out infinite",
                    "@keyframes pulseDot": {
                      "0%, 100%": { opacity: 1, transform: "scale(1)" },
                      "50%": { opacity: 0.4, transform: "scale(0.7)" },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: colors.state.danger.main,
                  }}
                >
                  DMA bypass — layer visibility
                </Typography>
              </Box>
              {DMA_LAYERS.map((layer, i) => (
                <React.Fragment key={layer.name}>
                  {i > 0 ? (
                    <Box
                      sx={{
                        height: 16,
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 20,
                          right: 20,
                          top: "50%",
                          height: "1px",
                          background: colors.border.subtle,
                        },
                      }}
                    />
                  ) : null}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      p: "16px 20px",
                      ...layer.layerSx,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: layer.nameColor,
                        }}
                      >
                        {layer.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.6875rem",
                          color: layer.detailColor || colors.text.muted,
                          mt: 0.25,
                        }}
                      >
                        {layer.detail}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.5625rem",
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        flexShrink: 0,
                        px: 1.125,
                        py: 0.375,
                        color:
                          layer.tagStyle === "blocked" ? colors.state.danger.main : colors.text.muted,
                        background:
                          layer.tagStyle === "blocked"
                            ? "rgba(220,38,38,0.1)"
                            : "rgba(115,115,125,0.3)",
                        border:
                          layer.tagStyle === "blocked"
                            ? "1px solid rgba(220,38,38,0.2)"
                            : "1px solid rgba(115,115,125,0.5)",
                      }}
                    >
                      {layer.tag}
                    </Typography>
                  </Box>
                </React.Fragment>
              ))}
              <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid rgba(220,38,38,0.2)" }}>
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: 700,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: colors.primary.main,
                    mb: 1,
                  }}
                >
                  Ouro server layer
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: colors.text.secondary, lineHeight: 1.6 }}>
                  Collects gameplay events independently on the server. No local access required — and no local
                  access possible. DMA reads the game&apos;s memory; the server records what the game actually did.
                </Typography>
              </Box>
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Enforcement"
            title="From data to action."
            description="The pipeline from live match event to enforcement decision — kept simple publicly, deliberately complex internally."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
              gap: "2px",
            }}
          >
            {ACTION_STEPS.map((step) => (
              <Box
                key={step.num}
                sx={{
                  ...cardSx,
                  borderRadius: 0,
                  p: { xs: "28px 24px", md: "36px 28px" },
                  position: "relative",
                  overflow: "hidden",
                  transition: "border-color .3s",
                  "&:hover": { borderColor: "rgba(232,163,61,0.25)" },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "35%",
                    height: "1px",
                    zIndex: 1,
                    pointerEvents: "none",
                    background: `
                      linear-gradient(
                        90deg,
                        transparent,
                        rgba(246,185,76,0.9),
                        transparent
                      )
                    `,
                    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    letterSpacing: "0.15em",
                    color: colors.primary.main,
                    mb: 2.5,
                  }}
                >
                  {step.num}
                </Typography>
                <SvgIcon sx={{ mb: 2 }}>{step.icon}</SvgIcon>
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.125rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    mb: 1.25,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: colors.text.secondary, lineHeight: 1.7 }}>
                  {step.body}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro label="FAQ" title="Common questions." />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              mt: 8,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              border: `1px solid ${colors.border.subtle}`,
            }}
          >
            {FAQ_ITEMS.map((item, i) => (
              <Box
                key={item.q}
                sx={{
                  p: { xs: "28px 24px", md: "36px 40px" },
                  borderBottom: {
                    xs: i < FAQ_ITEMS.length - 1 ? `1px solid ${colors.border.subtle}` : "none",
                    md: i < FAQ_ITEMS.length - 2 ? `1px solid ${colors.border.subtle}` : "none",
                  },
                  borderRight: {
                    xs: "none",
                    md: i % 2 === 0 ? `1px solid ${colors.border.subtle}` : "none",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    mb: 1.5,
                  }}
                >
                  {item.q}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.75 }}>
                  {item.a}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={{ ...sectionSx, pb: { xs: "80px", md: "120px" } }}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="The full picture"
            title="Anti-cheat is one layer."
            description="It works alongside trust, safety, and reputation — three interconnected systems where each compensates for what the others can't catch alone."
          />
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              ...cardSx,
              borderColor: "rgba(232,163,61,0.25)",
              background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
              p: { xs: "36px 28px", md: "56px 64px" },
              position: "relative",
              overflow: "hidden",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 0,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "1px",
                background: `linear-gradient(90deg, transparent, ${colors.primary.main}, transparent)`,
              },
            }}
          >
            {STACK_ITEMS.map((item, i) => (
              <Box
                key={item.name}
                sx={{
                  py: { xs: 3, md: 0 },
                  px: { md: i === 0 ? 0 : 5 },
                  borderRight: { md: i < 2 ? `1px solid ${colors.border.subtle}` : "none" },
                  borderBottom: { xs: i < 2 ? `1px solid ${colors.border.subtle}` : "none", md: "none" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Inter', system-ui, sans-serif",
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: colors.primary.main,
                    mb: 1.25,
                  }}
                >
                  {item.name}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: colors.text.secondary, lineHeight: 1.7, mb: 2 }}>
                  {item.body}
                </Typography>
                {item.active ? (
                  <Typography
                    sx={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: colors.primary.main,
                    }}
                  >
                    You are here
                  </Typography>
                ) : (
                  <Box
                    component={Link}
                    to={item.to}
                    sx={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: colors.text.muted,
                      textDecoration: "none",
                      "&:hover": { color: colors.primary.main },
                    }}
                  >
                    {item.linkLabel}
                  </Box>
                )}
              </Box>
            ))}
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer
          sx={{
            maxWidth: "75rem",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            alignItems: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
            <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>
              The server
              <br />
              sees everything.
              <br />
              Act accordingly.
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", color: colors.text.secondary, maxWidth: 400, lineHeight: 1.7 }}>
              Join the founding community. Every verified player who joins builds a stronger baseline for what
              legitimate play looks like — and makes the detection sharper for everyone.
            </Typography>
          </MotionBox>
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.12 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join the founding community
            </Box>
            <Box component={Link} to="/trust-and-safety" sx={btnGhostSx}>
              Trust &amp; Safety →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>
    </Box>
  );
}
